import { ExpandedEventInUrl } from '@src/app/enums';
import { statsService } from '@src/federations/sportOffer/competitions/services/StatsService';
import { SportOfferStore } from '@store/modules/ui/sportOffer/sportPage/sportPageIndex';
import { CompetitionDataStore } from '@sportOfferFederation/competitions/store/data';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    watch,
    ref,
    Ref,
} from '@vue/composition-api';
import {
    sportName as getSportName,
    categoryName as getCategoryName,
    tournamentName as getTournamentName,
    sportUrlName as getSportUrlName,
    categoryUrlName as getCategoryUrlName,
    tournamentUrlName as getTournamentUrlName,
} from '@modules/eventGrid/nameExtractors';
import { StatsEvent } from '@core/models/base/StatsEvent';

import {
    logger,
    slugify,
} from '@superbet-group/web.lib.utilities';
import { RouteName } from '@src/app/router/constants/RouteName';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { BreadcrumbItem } from '@shared/components/breadcrumbs/types';
import { StatsCompetition } from '@sportOfferFederation/competitions/types';
import { simplifyTournamentName } from '@core/utils/sportsOffer/simplifyTournamentName';
import { getShellConfig } from '@src/config/shell/config';
import { getFlagByCategory } from '@sportOfferFederation/flags/flagMapping';

const getRouteSlug = (param?: string) => typeof param === 'string' ? slugify(param).toLowerCase() : '';

export function useBreadcrumbs(statsEvent?: Ref<StatsEvent | null | undefined>) {
    const { route } = useVueRouter();
    const sportOfferUIStore = useVuexStore<SportOfferStore>('ui/sportOffer');
    const competitionsStore = useVuexStore<CompetitionDataStore>('data/competitions');

    const offerSportIdsMappedToStatsSportIds = ref<Record<string, number>>({});
    const statsEventTournament = ref<StatsCompetition | null>();

    const expandedEventData = sportOfferUIStore.getter('expandedEventData');
    const currentCompetition = competitionsStore.getter('currentVirtualTournament');

    const isStatsEventInQuery = computed(() => {
        return route.value?.query[ExpandedEventInUrl.type]?.includes('stats');
    });

    const betradarTournamentIdFromQuery = computed(() => {
        const typeQuery = route.value?.query[ExpandedEventInUrl.type];
        return typeof typeQuery === 'string' ? typeQuery.split('-').pop() : null;
    });

    const sportId = computed(() => {
        return expandedEventData.value?.sportId
            || Object.keys(offerSportIdsMappedToStatsSportIds.value)
                .find((key) => offerSportIdsMappedToStatsSportIds.value[key] === statsEventTournament.value?.categorySportId);
    });
    const categoryId = computed(() => {
        return expandedEventData.value?.categoryId
            || statsEventTournament.value?.categoryIds?.[0];
    });
    const tournamentId = computed(() => {
        const statsTournamentId = statsEventTournament.value?.betradarTournamentIds
            ?.find((brId) => brId.toString() === betradarTournamentIdFromQuery.value);

        return expandedEventData.value?.tournamentId
            || Number(statsTournamentId);
    });

    const sportName = computed(() => sportId.value ? getSportName(sportId.value) : '');
    const categoryName = computed(() => categoryId.value ? getCategoryName(categoryId.value) : '');
    const tournamentName = computed(() => {
        const originalTournamentName = tournamentId.value ? getTournamentName(tournamentId.value) as string : '';
        return simplifyTournamentName(originalTournamentName, categoryName.value);
    });
    const sportSlugName = computed(() => sportId.value ? getSportUrlName(sportId.value) : '');
    const categorySlugName = computed(() => categoryId.value ? getCategoryUrlName(categoryId.value) : '');
    const tournamentSlugName = computed(() => isStatsEventInQuery.value ? currentCompetitionName.value : (tournamentId.value && getTournamentUrlName(tournamentId.value)) ?? '');

    const currentCompetitionName = computed(() => {
        if (isStatsEventInQuery.value) {
            return statsEvent?.value?.competition?.name;
        }
        if (currentCompetition.value) {
            return currentCompetition.value.name;
        } else {
            return tournamentName.value;
        }
    });

    const breadcrumbs = computed(() => {
        const competitionQuery: Record<string, unknown> = {
            [ExpandedEventInUrl.competitionId]: statsEvent?.value?.competition?.id,
            [ExpandedEventInUrl.seasonId]: statsEvent?.value?.season?.id,
        };

        if (!getShellConfig().remoteConfig.sportsOffer.useSimplifiedTournamentName) {
            competitionQuery[ExpandedEventInUrl.tournamentId] = tournamentId.value;
        }

        return [
            {
                label: sportName.value,
                to: {
                    name: RouteName.Sport.CategoryPerTime,
                    params: {
                        sportSlug: getRouteSlug(sportSlugName.value),
                        timeFilter: TimeFilterSlug.Today,
                    },
                },
            },
            {
                label: categoryName.value,
                flag: getFlagByCategory(categoryId.value),
                to: {
                    name: RouteName.Sport.SportCompetitions,
                    params: {
                        sportSlug: getRouteSlug(sportSlugName.value),
                    },
                    query: {
                        categoryId: categoryId.value?.toString() ?? '',
                    },
                },
            },
            {
                label: currentCompetitionName.value,
                to: {
                    name: RouteName.Sport.Competition,
                    params: {
                        sportSlug: getRouteSlug(sportSlugName.value),
                        categorySlug: getRouteSlug(categorySlugName.value),
                        tournamentSlug: getRouteSlug(simplifyTournamentName(tournamentSlugName.value, categorySlugName.value)),
                        timeFilter: TimeFilterSlug.All,
                        tournamentId: tournamentId.value ?? '',
                    },
                    query: competitionQuery,
                },
            },
        ] as BreadcrumbItem[];
    });

    watch(betradarTournamentIdFromQuery, async (newBetradarTournamentId) => {
        if (isStatsEventInQuery.value) {
            try {
                const sportIdsFromStats = await import('stats/getSportIds');
                offerSportIdsMappedToStatsSportIds.value = sportIdsFromStats?.default()?.OfferSportIdsMappedToStatsSportIds;

                statsEventTournament.value = newBetradarTournamentId
                    ? await statsService.getCompetitionByTournamentBetradarId(parseInt(newBetradarTournamentId, 10)) : null;
            } catch (e) {
                logger.error(e as Error);
            }
        }
    }, { immediate: true });

    return {
        breadcrumbs,
        isStatsEventInQuery,
    };
}
